import React, { useState, useEffect } from 'react';
import './StylePromptsModal.css';
import { AutoCropSettings, DialogueBoxStyle } from '../types/storybook';
import { BookDesignStyle } from '../types/layout';

// Visual design style presets
type DesignStyleType = BookDesignStyle;

interface DesignStylePreset {
  id: DesignStyleType;
  name: string;
  description: string;
  image?: string;
  icon?: string;
  autoCropSettings: AutoCropSettings;
}

const designStylePresets: DesignStylePreset[] = [
  {
    id: 'full-bleed-caption',
    name: 'Full-bleed Caption',
    description: 'A short caption over a simple area; subject overlap is checked before export',
    image: '/screenshots/visual-styles/design-cartoon.png',
    autoCropSettings: {
      enabled: false,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      featherPercent: 0,
      cropShape: 'rect'
    }
  },
  {
    id: 'text-bottom',
    name: 'Protected Text Below',
    description: 'Recommended: image ends above an image-free reading panel',
    image: '/screenshots/visual-styles/design-story-bottom.png',
    autoCropSettings: {
      enabled: true,
      paddingTop: 0,
      paddingBottom: 38,
      paddingLeft: 0,
      paddingRight: 0,
      featherPercent: 0,
      cropShape: 'rect'
    }
  },
  {
    id: 'text-top',
    name: 'Protected Text Above',
    description: 'Image begins below an image-free reading panel',
    image: '/screenshots/visual-styles/design-story-top.png',
    autoCropSettings: {
      enabled: true,
      paddingTop: 38,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      featherPercent: 0,
      cropShape: 'rect'
    }
  },
  {
    id: 'split',
    name: 'Split Page',
    description: 'Illustration and text use separate side-by-side regions',
    icon: '◧',
    autoCropSettings: {
      enabled: true, paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 48,
      featherPercent: 0, cropShape: 'rect'
    }
  },
  {
    id: 'dialogue',
    name: 'Dialogue Panel',
    description: 'Character dialogue stays in a protected reading panel',
    icon: '💬',
    autoCropSettings: {
      enabled: true, paddingTop: 0, paddingBottom: 42, paddingLeft: 0, paddingRight: 0,
      featherPercent: 0, cropShape: 'rect'
    }
  },
  {
    id: 'title',
    name: 'Title Page',
    description: 'Large centered title with a separate illustration region',
    icon: 'T',
    autoCropSettings: {
      enabled: true, paddingTop: 0, paddingBottom: 42, paddingLeft: 0, paddingRight: 0,
      featherPercent: 0, cropShape: 'rect'
    }
  },
  {
    id: 'text-only',
    name: 'Text Only',
    description: 'Uses the complete safe page when an illustration is not needed',
    icon: '¶',
    autoCropSettings: {
      enabled: false, paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0,
      featherPercent: 0, cropShape: 'rect'
    }
  }
];

interface DesignSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    autoCropSettings?: AutoCropSettings;
    designStyle?: DesignStyleType;
    dialogueBoxStyle?: DialogueBoxStyle;
  }) => void;
  currentAutoCropSettings?: AutoCropSettings;
  currentDesignStyle?: DesignStyleType;
  currentDialogueBoxStyle?: DialogueBoxStyle;
}

const defaultAutoCropSettings: AutoCropSettings = {
  enabled: true,
  paddingTop: 0,
  paddingBottom: 38,
  paddingLeft: 0,
  paddingRight: 0,
  featherPercent: 0,
  cropShape: 'rect'
};

const defaultDialogueBoxStyle: DialogueBoxStyle = {
  backgroundColor: '#ffffff',
  backgroundOpacity: 80,
  borderWidth: 2,
  borderStyle: 'solid',
  borderColor: '#000000',
  borderRadius: 8,
  textColor: '#000000',
  styleId: undefined
};

const normalizeLegacyDesignStyle = (style: DesignStyleType): DesignStyleType =>
  style === 'full-scene' ? 'full-bleed-caption' : style;

const DesignSettingsModal: React.FC<DesignSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentAutoCropSettings,
  currentDesignStyle = 'text-bottom',
  currentDialogueBoxStyle
}) => {
  // Design style state
  const [selectedDesignStyle, setSelectedDesignStyle] = useState<DesignStyleType>(
    normalizeLegacyDesignStyle(currentDesignStyle)
  );
  const [showAdvancedCrop, setShowAdvancedCrop] = useState(false);
  
  // Auto-crop settings state
  const [autoCropSettings, setAutoCropSettings] = useState<AutoCropSettings>(
    currentAutoCropSettings || defaultAutoCropSettings
  );
  
  // Dialogue box styling state
  const [dialogueBoxStyle, setDialogueBoxStyle] = useState<DialogueBoxStyle>(
    currentDialogueBoxStyle || defaultDialogueBoxStyle
  );

  // Sync with props when they change (e.g., when modal opens)
  useEffect(() => {
    setAutoCropSettings(currentAutoCropSettings || defaultAutoCropSettings);
    setSelectedDesignStyle(normalizeLegacyDesignStyle(currentDesignStyle || 'text-bottom'));
    setDialogueBoxStyle(currentDialogueBoxStyle || defaultDialogueBoxStyle);
    setShowAdvancedCrop(false);
  }, [currentAutoCropSettings, currentDesignStyle, currentDialogueBoxStyle]);

  // Handle design style selection - auto-configure crop settings
  const handleDesignStyleSelect = (styleId: DesignStyleType) => {
    setSelectedDesignStyle(styleId);
    
    if (styleId === 'custom') {
      // For custom, enable advanced settings and use current values
      setShowAdvancedCrop(true);
      setAutoCropSettings(prev => ({ ...prev, enabled: true }));
    } else {
      // Find preset and apply its settings
      const preset = designStylePresets.find(p => p.id === styleId);
      if (preset) {
        setAutoCropSettings(preset.autoCropSettings);
        setShowAdvancedCrop(false);
      }
    }
    
    // For Full Scene, use speech bubble style for text boxes
    if (styleId === 'full-scene' || styleId === 'full-bleed-caption' || styleId === 'dialogue') {
      setDialogueBoxStyle(prev => ({ ...prev, styleId: 'speech-bubble', borderStyle: 'dashed' }));
    } else {
      // For other styles, use regular rectangle box
      setDialogueBoxStyle(prev => ({ ...prev, styleId: undefined, borderStyle: 'solid' }));
    }
  };

  const handleSave = () => {
    onSave({
      autoCropSettings,
      designStyle: selectedDesignStyle,
      dialogueBoxStyle
    });
    onClose();
  };

  // Helper to update auto-crop settings
  const updateAutoCropSetting = <K extends keyof AutoCropSettings>(
    key: K,
    value: AutoCropSettings[K]
  ) => {
    setAutoCropSettings(prev => ({ ...prev, [key]: value }));
  };

  // Helper to update dialogue box style
  const updateDialogueBoxStyle = <K extends keyof DialogueBoxStyle>(
    key: K,
    value: DialogueBoxStyle[K]
  ) => {
    setDialogueBoxStyle(prev => ({ ...prev, [key]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="style-prompts-overlay">
      <div className="style-prompts-modal">
        <div className="style-prompts-header">
          <h2>🎨 Design Settings</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="style-prompts-content">
          {/* Visual Design Style Selection */}
          <div className="style-section">
            <h3>🖼️ Page Layout Style</h3>
            <p className="section-description">
              Choose how images and text are arranged on each page
            </p>
            
            <div className="design-style-grid">
              {designStylePresets.map((preset) => (
                <div
                  key={preset.id}
                  className={`design-style-card ${selectedDesignStyle === preset.id ? 'selected' : ''}`}
                  onClick={() => handleDesignStyleSelect(preset.id)}
                >
                  <div className="design-style-image">
                    {preset.image
                      ? <img src={preset.image} alt={preset.name} />
                      : <span className="custom-icon" aria-hidden="true">{preset.icon}</span>}
                  </div>
                  <div className="design-style-info">
                    <h4>{preset.name}</h4>
                    <p>{preset.description}</p>
                  </div>
                  {selectedDesignStyle === preset.id && (
                    <div className="design-style-check">✓</div>
                  )}
                </div>
              ))}
              
              {/* Custom option */}
              <div
                className={`design-style-card custom-card ${selectedDesignStyle === 'custom' ? 'selected' : ''}`}
                onClick={() => handleDesignStyleSelect('custom')}
              >
                <div className="design-style-image custom-icon">
                  <span>⚙️</span>
                </div>
                <div className="design-style-info">
                  <h4>Custom</h4>
                  <p>Configure your own layout settings</p>
                </div>
                {selectedDesignStyle === 'custom' && (
                  <div className="design-style-check">✓</div>
                )}
              </div>
            </div>
          </div>

          {/* Advanced Settings (collapsible) */}
          <div className="style-section auto-crop-section">
            <div 
              className="advanced-toggle-header"
              onClick={() => setShowAdvancedCrop(!showAdvancedCrop)}
            >
              <h3>⚙️ Advanced Settings</h3>
              <span className={`toggle-arrow ${showAdvancedCrop ? 'open' : ''}`}>▼</span>
            </div>
            
            {showAdvancedCrop && (
              <div className="auto-crop-controls">
                <p className="section-description">
                  Fine-tune how scenes are cropped and positioned
                </p>
                
                <div className="auto-crop-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={autoCropSettings.enabled}
                      onChange={(e) => {
                        updateAutoCropSetting('enabled', e.target.checked);
                        if (e.target.checked) setSelectedDesignStyle('custom');
                      }}
                    />
                    <span className="toggle-text">Enable auto-crop for scene images</span>
                  </label>
                </div>

                {autoCropSettings.enabled && (
                  <>
                    {/* Crop Shape Selection */}
                    <div className="crop-shape-row">
                      <label className="crop-shape-label">Crop Shape:</label>
                      <div className="crop-shape-options">
                        <label className="shape-option">
                          <input
                            type="radio"
                            name="cropShape"
                            value="rect"
                            checked={autoCropSettings.cropShape === 'rect'}
                            onChange={(e) => updateAutoCropSetting('cropShape', e.target.value as 'rect' | 'ellipse')}
                          />
                          <span>Rectangle</span>
                        </label>
                        <label className="shape-option">
                          <input
                            type="radio"
                            name="cropShape"
                            value="ellipse"
                            checked={autoCropSettings.cropShape === 'ellipse'}
                            onChange={(e) => updateAutoCropSetting('cropShape', e.target.value as 'rect' | 'ellipse')}
                          />
                          <span>Ellipse</span>
                        </label>
                      </div>
                    </div>

                    {/* Padding Controls */}
                    <div className="padding-controls">
                      <h4>Padding (% of canvas size)</h4>
                      <div className="padding-grid">
                        <div className="padding-item">
                          <label>Top</label>
                          <input
                            type="range"
                            min="0"
                            max="50"
                            value={autoCropSettings.paddingTop}
                            onChange={(e) => {
                              updateAutoCropSetting('paddingTop', Number(e.target.value));
                              setSelectedDesignStyle('custom');
                            }}
                          />
                          <span className="padding-value">{autoCropSettings.paddingTop}%</span>
                        </div>
                        <div className="padding-item">
                          <label>Bottom</label>
                          <input
                            type="range"
                            min="0"
                            max="50"
                            value={autoCropSettings.paddingBottom}
                            onChange={(e) => {
                              updateAutoCropSetting('paddingBottom', Number(e.target.value));
                              setSelectedDesignStyle('custom');
                            }}
                          />
                          <span className="padding-value">{autoCropSettings.paddingBottom}%</span>
                        </div>
                        <div className="padding-item">
                          <label>Left</label>
                          <input
                            type="range"
                            min="0"
                            max="50"
                            value={autoCropSettings.paddingLeft}
                            onChange={(e) => {
                              updateAutoCropSetting('paddingLeft', Number(e.target.value));
                              setSelectedDesignStyle('custom');
                            }}
                          />
                          <span className="padding-value">{autoCropSettings.paddingLeft}%</span>
                        </div>
                        <div className="padding-item">
                          <label>Right</label>
                          <input
                            type="range"
                            min="0"
                            max="50"
                            value={autoCropSettings.paddingRight}
                            onChange={(e) => {
                              updateAutoCropSetting('paddingRight', Number(e.target.value));
                              setSelectedDesignStyle('custom');
                            }}
                          />
                          <span className="padding-value">{autoCropSettings.paddingRight}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Feather Effect Control */}
                    <div className="feather-control">
                      <h4>Edge Feather Effect</h4>
                      <div className="feather-slider-row">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={autoCropSettings.featherPercent}
                          onChange={(e) => updateAutoCropSetting('featherPercent', Number(e.target.value))}
                          className="feather-slider"
                        />
                        <span className="feather-value">{autoCropSettings.featherPercent}%</span>
                      </div>
                      <p className="help-text">Softens edges for smooth blending with backgrounds</p>
                    </div>
                  </>
                )}

                {/* Advanced Dialogue Box Styling */}
                <div className="dialogue-box-style-section">
                  <h4>🎨 Advanced Dialogue Box Styling</h4>
                  <p className="section-description">
                    Customize the appearance of character speech boxes (only applies to dialogue, not narration)
                  </p>
                  
                  <div className="dialogue-box-preview">
                    <div 
                      className="dialogue-box-sample"
                      style={{
                        backgroundColor: (() => {
                          // Convert hex to rgba with opacity
                          const hex = dialogueBoxStyle.backgroundColor.replace('#', '');
                          const r = parseInt(hex.substring(0, 2), 16);
                          const g = parseInt(hex.substring(2, 4), 16);
                          const b = parseInt(hex.substring(4, 6), 16);
                          return `rgba(${r}, ${g}, ${b}, ${dialogueBoxStyle.backgroundOpacity / 100})`;
                        })(),
                        borderWidth: `${dialogueBoxStyle.borderWidth}px`,
                        borderStyle: dialogueBoxStyle.borderStyle,
                        borderColor: dialogueBoxStyle.borderColor,
                        borderRadius: `${dialogueBoxStyle.borderRadius}px`,
                        padding: '12px 16px',
                        color: dialogueBoxStyle.textColor
                      }}
                    >
                      "Hello! I'm an example dialogue box!"
                    </div>
                  </div>

                  <div className="dialogue-box-controls">
                    <div className="control-row">
                      <div className="control-group">
                        <label>Background Color</label>
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            value={dialogueBoxStyle.backgroundColor}
                            onChange={(e) => updateDialogueBoxStyle('backgroundColor', e.target.value)}
                          />
                          <input
                            type="text"
                            value={dialogueBoxStyle.backgroundColor}
                            onChange={(e) => updateDialogueBoxStyle('backgroundColor', e.target.value)}
                            className="color-text-input"
                          />
                        </div>
                      </div>
                      <div className="control-group">
                        <label>Opacity ({dialogueBoxStyle.backgroundOpacity}%)</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={dialogueBoxStyle.backgroundOpacity}
                          onChange={(e) => updateDialogueBoxStyle('backgroundOpacity', parseInt(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className="control-row">
                      <div className="control-group">
                        <label>Border Width ({dialogueBoxStyle.borderWidth}px)</label>
                        <input
                          type="range"
                          min="0"
                          max="10"
                          value={dialogueBoxStyle.borderWidth}
                          onChange={(e) => updateDialogueBoxStyle('borderWidth', parseInt(e.target.value))}
                        />
                      </div>
                      <div className="control-group">
                        <label>Border Style</label>
                        <select
                          value={dialogueBoxStyle.borderStyle}
                          onChange={(e) => updateDialogueBoxStyle('borderStyle', e.target.value as 'none' | 'solid' | 'dashed' | 'dotted' | 'double')}
                        >
                          <option value="none">None</option>
                          <option value="solid">Solid</option>
                          <option value="dashed">Dashed</option>
                          <option value="dotted">Dotted</option>
                          <option value="double">Double</option>
                        </select>
                      </div>
                    </div>

                    <div className="control-row">
                      <div className="control-group">
                        <label>Border Color</label>
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            value={dialogueBoxStyle.borderColor}
                            onChange={(e) => updateDialogueBoxStyle('borderColor', e.target.value)}
                          />
                          <input
                            type="text"
                            value={dialogueBoxStyle.borderColor}
                            onChange={(e) => updateDialogueBoxStyle('borderColor', e.target.value)}
                            className="color-text-input"
                          />
                        </div>
                      </div>
                      <div className="control-group">
                        <label>Border Radius ({dialogueBoxStyle.borderRadius}px)</label>
                        <input
                          type="range"
                          min="0"
                          max="30"
                          value={dialogueBoxStyle.borderRadius}
                          onChange={(e) => updateDialogueBoxStyle('borderRadius', parseInt(e.target.value))}
                        />
                      </div>
                    </div>

                    <div className="control-row">
                      <div className="control-group">
                        <label>Text Color</label>
                        <div className="color-input-wrapper">
                          <input
                            type="color"
                            value={dialogueBoxStyle.textColor}
                            onChange={(e) => updateDialogueBoxStyle('textColor', e.target.value)}
                          />
                          <input
                            type="text"
                            value={dialogueBoxStyle.textColor}
                            onChange={(e) => updateDialogueBoxStyle('textColor', e.target.value)}
                            className="color-text-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="style-prompts-actions">
          <button onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn btn-primary">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesignSettingsModal;
